import { Box, makeStyles, Tab, TabProps, Tabs, TabsProps } from '@material-ui/core';
import { getPanelId, getTabId, TabContext, TabPanel, TabPanelProps, useTabContext } from '@material-ui/lab';
import { CreateCSSProperties } from '@material-ui/styles';
import clsx from 'clsx';
import { ChangeEvent, useState } from 'react';

import { FlexRow } from '@/components/shared/components/FlexRow';
import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { Text } from '@/components/shared/components/Text';
import { useCustomTheme } from '@/hooks/useCustomTheme';

export type CustomTab = {
  name: string;
  panel: JSX.Element;
  label?: JSX.Element;
  iconName?: string;
  endAdornment?: JSX.Element;
  keepMounted?: boolean;
  tabProps?: TabProps;
  tabPanelProps?: TabPanelProps;
};

interface KeepMountedTabPanelProps {
  tab: CustomTab;
  selectedTab: string;
  tabPanelClassName: string;
}

const KeepMountedTabPanel = ({ tab, selectedTab, tabPanelClassName }: KeepMountedTabPanelProps) => {
  const context = useTabContext();

  if (!context) {
    return null;
  }

  const isActive = selectedTab === tab.name;
  const { className, value: _value, style, ...tabPanelProps } = tab.tabPanelProps ?? {};

  return (
    <Box
      role="tabpanel"
      hidden={!isActive}
      aria-hidden={!isActive}
      id={getPanelId(context, tab.name) ?? undefined}
      aria-labelledby={getTabId(context, tab.name) ?? undefined}
      className={clsx(tabPanelClassName, className)}
      style={{
        ...style,
        ...(isActive ? {} : { display: 'none' }),
      }}
      {...tabPanelProps}
    >
      {tab.panel}
    </Box>
  );
};

interface CustomTabsProps extends Omit<TabsProps, 'defaultValue' | 'value'> {
  defaultValue?: string;
  value?: string;
  tabs: CustomTab[];
  tabClassProps?: {
    tabs?: { root: CreateCSSProperties };
    tab?: { root: CreateCSSProperties };
    tabPanel?: { root: CreateCSSProperties };
  };
  onValueChange?: (newValue: string) => void;
}
export const CustomTabs = ({
  tabs,
  tabClassProps,
  defaultValue,
  value,
  onValueChange,
  ...tabsProps
}: CustomTabsProps) => {
  const { palette } = useCustomTheme();

  const [internalSelectedTab, setInternalSelectedTab] = useState<string>(
    defaultValue ?? tabs.find((tab) => tab.tabProps?.selected)?.name ?? tabs[0].name,
  );
  const selectedTab = value ?? internalSelectedTab;

  const handleChange = (_: ChangeEvent<object>, newValue: string) => {
    if (onValueChange) {
      onValueChange(newValue);
    }
    if (value === undefined) {
      setInternalSelectedTab(newValue);
    }
  };

  const tabsClasses = makeStyles({
    root: {
      minHeight: 0,
      padding: 0,
      backgroundColor: 'transparent',
      borderRadius: 0,
      boxShadow: 'none',
      ...tabClassProps?.tabs?.root,
    },
    indicator: {
      height: 4,
      borderRadius: 2,
      backgroundColor: palette.primary.main,
      bottom: 0,
    },
  })();

  const tabClasses = makeStyles({
    root: {
      minHeight: '32px',
      padding: '10px 16px',
      borderRadius: 0,
      textTransform: 'none',
      fontWeight: 400,
      color: palette.text.primary,
      transition: 'font-weight 0.2s',
      ...tabClassProps?.tab?.root,
    },
  })();

  const tabPanelClasses = makeStyles({
    root: {
      paddingTop: 24,
      paddingInline: 0,
      borderTop: `1px solid ${palette.grey[200]}`,
      ...tabClassProps?.tabPanel?.root,
    },
  })();

  return (
    <TabContext value={selectedTab}>
      <Tabs
        classes={tabsClasses}
        value={selectedTab}
        onChange={handleChange}
        variant="scrollable"
        scrollButtons="auto"
        indicatorColor="secondary"
        textColor="secondary"
        defaultValue={selectedTab}
        {...tabsProps}
      >
        {tabs.map((tab) => (
          <Tab
            key={`tab-${tab.name}`}
            classes={tabClasses}
            value={tab.name}
            label={
              <FlexRow minWidth="0">
                {tab.label ? (
                  tab.label
                ) : (
                  <Font
                    variant={'body2'}
                    style={{
                      fontWeight: tab.name === selectedTab ? 700 : 500,
                      color: tab.name === selectedTab ? palette.text.title : palette.text.secondary,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gridGap: 8,
                    }}
                  >
                    {tab.iconName && <Icon size={16}>{tab.iconName}</Icon>}
                    <Text tId={`${tab.name}_TAB_LABEL`} />
                  </Font>
                )}
                {tab.endAdornment && <>{tab.endAdornment}</>}
              </FlexRow>
            }
            {...tab.tabProps}
          />
        ))}
      </Tabs>
      {tabs.map((tab) =>
        tab.keepMounted ? (
          <KeepMountedTabPanel
            key={`tab-panel-${tab.name}`}
            tab={tab}
            selectedTab={selectedTab}
            tabPanelClassName={tabPanelClasses.root}
          />
        ) : (
          <TabPanel key={`tab-panel-${tab.name}`} classes={tabPanelClasses} value={tab.name} {...tab.tabPanelProps}>
            {tab.panel}
          </TabPanel>
        ),
      )}
    </TabContext>
  );
};
